import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiArrowLeft, FiBell, FiCheck, FiFilter, FiRefreshCw, FiTrash2, FiEye } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import NotificationItem from '../components/NotificationItem';
import { 
  getUserNotifications, 
  markAllNotificationsAsRead, 
  NOTIFICATION_TYPES 
} from '../services/notificationService';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';

function NotificationCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { unreadCount, setUnreadCount } = useNotifications();
  
  // State
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread, rounds, groups
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Filter options
  const filterOptions = [
    { value: 'all', label: 'All', icon: FiBell },
    { value: 'unread', label: 'Unread', icon: FiEye }
  ];

  // Fetch notifications
  const fetchNotifications = async (refresh = false) => {
    if (!user) return;

    if (refresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      // Build filter parameters
      const params = {
        limit: 50,
        offset: refresh ? 0 : 0
      };

      // Apply filters
      if (filter === 'unread') {
        params.read = false;
      }

      const { data, error } = await getUserNotifications(params);

      if (!error) {
        if (refresh) {
          setNotifications(data);
        } else {
          setNotifications(data);
        }
        setHasMore(data.length === 50);
      }

      // Unread count is now managed by NotificationContext

    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Load more notifications
  const loadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const params = {
        limit: 50,
        offset: notifications.length
      };

      // Apply same filters
      if (filter === 'unread') {
        params.read = false;
      }

      const { data, error } = await getUserNotifications(params);

      if (!error) {
        setNotifications(prev => [...prev, ...data]);
        setHasMore(data.length === 50);
      }
    } catch (error) {
      console.error('Error loading more notifications:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle notification update
  const handleNotificationUpdate = (updatedNotification, deletedId) => {
    if (updatedNotification === null && deletedId) {
      // Notification was deleted
      setNotifications(prev => prev.filter(n => n.id !== deletedId));
    } else if (updatedNotification) {
      // Notification was updated
      setNotifications(prev => 
        prev.map(n => n.id === updatedNotification.id ? updatedNotification : n)
      );
      
      // Unread count is now managed by the NotificationContext through NotificationItem
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    setRefreshing(true);
    const { error } = await markAllNotificationsAsRead();
    
    if (!error) {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    }
    
    setRefreshing(false);
  };

  // Handle filter change
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setNotifications([]);
    setHasMore(true);
  };

  // Initial load
  useEffect(() => {
    fetchNotifications();
  }, [user, filter]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchNotifications(true);
    }, 60000);

    return () => clearInterval(interval);
  }, [filter]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 100, damping: 15 } }
  };

  return (
    <div className="min-h-screen bg-app">
      <Helmet>
        <title>Notifications - TeeSync</title>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(-1)}
                className="p-2 text-text-secondary hover:text-text-primary transition-colors duration-200"
              >
                <SafeIcon icon={FiArrowLeft} className="w-6 h-6" />
              </motion.button>
              
              <div>
                <h1 className="text-2xl font-bold text-text-primary">Notifications</h1>
              </div>
            </div>

            {/* Actions - Desktop only */}
            <div className="hidden sm:flex items-center space-x-2">
              {unreadCount > 0 && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleMarkAllAsRead}
                  disabled={refreshing}
                  className="flex items-center space-x-2 px-4 py-2 bg-accent text-white rounded-md hover:bg-interactive-hover transition-colors duration-200 disabled:opacity-50"
                >
                  <SafeIcon icon={FiCheck} className="w-4 h-4" />
                  <span>Mark All Read</span>
                </motion.button>
              )}
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => fetchNotifications(true)}
                disabled={refreshing}
                className="p-2 text-text-secondary hover:text-text-primary transition-colors duration-200 disabled:opacity-50"
              >
                <SafeIcon 
                  icon={FiRefreshCw} 
                  className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} 
                />
              </motion.button>
            </div>
            
            {/* Mobile Refresh Button */}
            <div className="sm:hidden">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => fetchNotifications(true)}
                disabled={refreshing}
                className="p-2 text-text-secondary hover:text-text-primary transition-colors duration-200 disabled:opacity-50"
              >
                <SafeIcon 
                  icon={FiRefreshCw} 
                  className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} 
                />
              </motion.button>
            </div>
          </motion.div>

          {/* Filters */}
          <motion.div variants={itemVariants} className="bg-surface rounded-lg border border-border-subtle shadow-card p-6">
            <div className="flex gap-2 sm:flex-wrap">
              {filterOptions.map((option) => (
                <motion.button
                  key={option.value}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleFilterChange(option.value)}
                  className={`
                    flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 flex-1 sm:flex-none
                    ${filter === option.value 
                      ? 'bg-accent text-white ' 
                      : 'bg-surface text-text-secondary hover:bg-card'
                    }
                  `}
                >
                  <SafeIcon icon={option.icon} className="w-4 h-4" />
                  <span>{option.label}</span>
                </motion.button>
              ))}
            </div>
            
            {/* Mark All Read Button - Mobile only, below filters */}
            {unreadCount > 0 && (
              <div className="sm:hidden mt-4 pt-4 border-t border-border-subtle">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleMarkAllAsRead}
                  disabled={refreshing}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover transition-colors duration-200 disabled:opacity-50"
                >
                  <SafeIcon icon={FiCheck} className="w-4 h-4" />
                  <span>Mark All Read</span>
                </motion.button>
              </div>
            )}
          </motion.div>

          {/* Notifications List */}
          <motion.div variants={itemVariants}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-text-secondary">Loading notifications...</p>
                </div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-surface rounded-lg flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiBell} className="w-8 h-8 text-text-muted" />
                </div>
                <h3 className="text-lg font-medium text-text-primary mb-2">No notifications</h3>
                <p className="text-text-secondary">
                  {filter === 'unread' 
                    ? "You're all caught up! No unread notifications."
                    : "You don't have any notifications yet."
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <AnimatePresence mode="popLayout">
                  {notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onUpdate={handleNotificationUpdate}
                      showActions={true}
                      compact={false}
                    />
                  ))}
                </AnimatePresence>

                {/* Load More Button */}
                {hasMore && (
                  <div className="flex justify-center pt-6">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="px-6 py-3 text-accent border border-accent rounded-md hover:bg-accent/10 transition-all duration-200 disabled:opacity-50"
                    >
                      {loadingMore ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
                          <span>Loading...</span>
                        </div>
                      ) : (
                        'Load More'
                      )}
                    </motion.button>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

export default NotificationCenter;